import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import ReactMarkdown from "react-markdown";
import {
  Loader2, Clock, ArrowLeft, ArrowRight, ChevronDown, ChevronRight,
  CheckCircle2, Mail, ShieldCheck, Download,
} from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import PdfLeadGate from "../components/PdfLeadGate";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

function injectJsonLd(obj, id) {
  if (typeof document === "undefined") return;
  let tag = document.getElementById(id);
  if (!tag) {
    tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.id = id;
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(obj);
}

export default function BlogDetailPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    setLoading(true);
    setData(null);
    window.scrollTo(0, 0);
    api
      .get(`/blogs/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!data || !data.blog) return;
    const b = data.blog;
    // Article JSON-LD (provided by backend)
    if (data.jsonld) injectJsonLd(data.jsonld, "ld-article");

    // FAQ JSON-LD
    if (b.faq && b.faq.length > 0) {
      injectJsonLd(
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: b.faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
        "ld-blog-faq",
      );
    }
    // Breadcrumbs
    injectJsonLd(
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
          { "@type": "ListItem", position: 2, name: "Blog", item: `${BASE}/blog` },
          { "@type": "ListItem", position: 3, name: b.title, item: `${BASE}/blog/${b.slug}` },
        ],
      },
      "ld-blog-breadcrumb",
    );
    return () => {
      ["ld-article", "ld-blog-faq", "ld-blog-breadcrumb"].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [data]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="blog-detail-loading">
        <Loader2 className="animate-spin text-nibro-sage-dark" />
      </div>
    );
  }

  if (!data || !data.blog) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-24 text-center" data-testid="blog-not-found">
        <h1 className="text-3xl font-bold text-zinc-900">Article not found</h1>
        <p className="mt-3 text-zinc-600">This blog post does not exist (yet).</p>
        <Link
          to="/blog"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-nibro-sage px-5 py-2.5 text-sm font-semibold text-white hover:bg-nibro-sage-dark"
        >
          <ArrowLeft size={14} /> Back to all articles
        </Link>
      </div>
    );
  }

  const b = data.blog;
  const related = data.related_products || [];

  return (
    <div data-testid="blog-detail-page">
      <Helmet>
        <title>{`${b.title} — NIBRO by ARAMFIX`}</title>
        <meta name="description" content={b.meta_description} />
        <meta property="og:title" content={b.title} />
        <meta property="og:description" content={b.meta_description} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={b.hero_image} />
        <meta property="og:url" content={`${BASE}/blog/${b.slug}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="keywords" content={(b.keywords || []).join(", ")} />
        <link rel="canonical" href={`${BASE}/blog/${b.slug}`} />
      </Helmet>

      {/* Hero */}
      <section
        className="border-b-2 border-aramfix-red/30 px-5 py-16 lg:px-10 lg:py-24"
        style={{ backgroundColor: "var(--nibro-sage)" }}
      >
        <div className="mx-auto max-w-[1000px] text-white">
          <Link
            to="/blog"
            className="mb-6 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-white/80 hover:text-white"
            data-testid="back-to-blog-link"
          >
            <ArrowLeft size={12} /> All articles
          </Link>
          <div className="mb-4 inline-block rounded-full bg-aramfix-red px-3 py-1 text-[10px] font-bold uppercase tracking-wider">
            {b.category}
          </div>
          <h1 className="max-w-4xl text-3xl font-extrabold leading-tight sm:text-4xl lg:text-5xl">
            {b.title}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">{b.meta_description}</p>
          <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-white/80">
            <span className="inline-flex items-center gap-1.5">
              <Clock size={14} /> {b.reading_time_min} min read
            </span>
            <span>
              Published{" "}
              {new Date(b.published_at).toLocaleDateString("en-GB", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck size={14} /> NIBRO Engineering Team
            </span>
          </div>
          <PdfLeadGate
            kind="blog"
            slug={b.slug}
            title={b.title}
            pdfUrl={`${BASE}/api/blogs/${b.slug}/whitepaper.pdf`}
          >
            {(onClick) => (
              <a
                href={`${BASE}/api/blogs/${b.slug}/whitepaper.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClick}
                data-testid="whitepaper-download-hero"
                className="mt-7 inline-flex items-center gap-2 rounded-md bg-white px-5 py-3 text-sm font-bold uppercase tracking-wider text-nibro-sage-dark shadow-lg transition hover:bg-zinc-100"
              >
                <Download size={16} /> Download as PDF whitepaper
              </a>
            )}
          </PdfLeadGate>
        </div>
      </section>

      {/* Hero image */}
      <div className="mx-auto -mt-12 max-w-[1000px] px-5 lg:px-10">
        <div className="overflow-hidden rounded-xl border border-zinc-200 shadow-2xl">
          <img
            src={b.hero_image}
            alt={b.title}
            className="block aspect-[16/9] w-full object-cover"
          />
        </div>
      </div>

      {/* Body */}
      <article className="mx-auto max-w-[760px] px-5 py-14 lg:px-0 lg:py-20" data-testid="blog-article-body">
        <div className="prose prose-zinc max-w-none prose-headings:font-bold prose-headings:text-zinc-900 prose-h2:mt-12 prose-h2:text-2xl prose-h2:border-b prose-h2:border-zinc-200 prose-h2:pb-3 prose-h3:mt-8 prose-h3:text-xl prose-p:text-zinc-700 prose-p:leading-relaxed prose-strong:text-zinc-900 prose-a:text-aramfix-red prose-a:no-underline hover:prose-a:underline prose-ul:my-6 prose-li:text-zinc-700 prose-table:text-sm prose-th:bg-zinc-100 prose-th:text-left prose-td:border-zinc-200 prose-blockquote:border-l-4 prose-blockquote:border-nibro-sage prose-blockquote:bg-zinc-50 prose-blockquote:px-5 prose-blockquote:py-1 prose-blockquote:italic prose-blockquote:text-zinc-700">
          <ReactMarkdown>{b.body}</ReactMarkdown>
        </div>

        {/* Keywords */}
        {b.keywords && b.keywords.length > 0 && (
          <div className="mt-12 flex flex-wrap gap-2 border-t border-zinc-200 pt-8">
            {b.keywords.map((k) => (
              <span
                key={k}
                className="rounded-full bg-nibro-sage/10 px-3 py-1 text-xs font-semibold text-nibro-sage-dark"
              >
                #{k}
              </span>
            ))}
          </div>
        )}

        {/* In-flow whitepaper download */}
        <div
          className="mt-12 flex flex-col items-start gap-5 rounded-xl border-2 border-dashed border-nibro-sage bg-nibro-sage/5 p-6 sm:flex-row sm:items-center sm:justify-between"
          data-testid="whitepaper-download-inline"
        >
          <div className="flex items-start gap-4">
            <div className="rounded-lg bg-nibro-sage-dark p-3 text-white">
              <Download size={22} />
            </div>
            <div>
              <h3 className="text-base font-bold text-zinc-900">
                Take this article into your next project meeting
              </h3>
              <p className="mt-1 text-sm text-zinc-600">
                Download the full NIBRO-branded whitepaper PDF — printable, A4, with QR back-link.
              </p>
            </div>
          </div>
          <PdfLeadGate
            kind="blog"
            slug={b.slug}
            title={b.title}
            pdfUrl={`${BASE}/api/blogs/${b.slug}/whitepaper.pdf`}
          >
            {(onClick) => (
              <a
                href={`${BASE}/api/blogs/${b.slug}/whitepaper.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClick}
                data-testid="whitepaper-download-button"
                className="inline-flex flex-shrink-0 items-center gap-2 rounded-md bg-aramfix-red px-5 py-3 text-sm font-bold uppercase tracking-wider text-white shadow-sm transition hover:bg-aramfix-red-hover"
              >
                <Download size={14} /> Download PDF
              </a>
            )}
          </PdfLeadGate>
        </div>
      </article>

      {/* CTA */}
      {b.cta && (
        <section className="px-5 pb-16 lg:px-10" data-testid="blog-cta-section">
          <div className="mx-auto max-w-[1000px] overflow-hidden rounded-2xl bg-gradient-to-br from-nibro-sage-dark to-nibro-sage p-8 text-white shadow-xl lg:p-12">
            <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
              <div>
                <h3 className="text-2xl font-bold sm:text-3xl">
                  Need precision-grade pipe clamps for your project?
                </h3>
                <p className="mt-2 max-w-2xl text-white/85">
                  Factory-direct from Oisterwijk · EN 10204 3.1 traceability · 5–12 day delivery across Europe.
                </p>
              </div>
              <Link
                to={b.cta.to}
                data-testid="blog-cta-button"
                className="inline-flex items-center gap-2 rounded-md bg-white px-6 py-3 text-sm font-bold uppercase tracking-wider text-nibro-sage-dark transition hover:bg-zinc-100"
              >
                {b.cta.label} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {b.faq && b.faq.length > 0 && (
        <section className="border-t border-zinc-200 bg-zinc-50 px-5 py-16 lg:px-10 lg:py-20" data-testid="blog-faq-section">
          <div className="mx-auto max-w-[900px]">
            <h2 className="text-3xl font-bold text-zinc-900">Frequently asked questions</h2>
            <p className="mt-2 text-zinc-600">From engineers, procurement teams and fabricators.</p>
            <div className="mt-8 space-y-3">
              {b.faq.map((f, i) => {
                const open = openFaq === i;
                return (
                  <div
                    key={i}
                    className="overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm"
                    data-testid={`blog-faq-item-${i}`}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(open ? -1 : i)}
                      className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                      data-testid={`blog-faq-toggle-${i}`}
                    >
                      <span className="font-semibold text-zinc-900">{f.q}</span>
                      {open ? (
                        <ChevronDown size={18} className="flex-shrink-0 text-nibro-sage-dark" />
                      ) : (
                        <ChevronRight size={18} className="flex-shrink-0 text-zinc-400" />
                      )}
                    </button>
                    {open && (
                      <div className="border-t border-zinc-100 px-5 py-4 text-sm leading-relaxed text-zinc-700">
                        {f.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Related products */}
      {related.length > 0 && (
        <section className="mx-auto max-w-[1200px] px-5 py-16 lg:px-10 lg:py-20" data-testid="blog-related-products">
          <div className="mb-8 flex items-end justify-between">
            <div>
              <h2 className="text-3xl font-bold text-zinc-900">Related NIBRO products</h2>
              <p className="mt-1 text-zinc-600">In stock · factory-direct from Oisterwijk</p>
            </div>
            <Link
              to="/products"
              className="hidden text-sm font-semibold text-aramfix-red hover:underline sm:inline-flex sm:items-center sm:gap-1"
            >
              All products <ArrowRight size={12} />
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {related.slice(0, 6).map((p) => (
              <ProductCard key={p.id} p={p} />
            ))}
          </div>
        </section>
      )}

      {/* Trust strip */}
      <section className="border-t border-zinc-200 bg-white px-5 py-12 lg:px-10" data-testid="blog-trust-strip">
        <div className="mx-auto grid max-w-[1200px] grid-cols-2 gap-6 sm:grid-cols-4">
          {[
            { icon: ShieldCheck, label: "EN 10204 3.1" },
            { icon: CheckCircle2, label: "316L / 304L Stock" },
            { icon: Clock, label: "5–12 day EU delivery" },
            { icon: Mail, label: "info@aramfix.com" },
          ].map((t, i) => (
            <div key={i} className="flex items-center gap-3 text-sm font-semibold text-zinc-700">
              <t.icon size={20} className="text-nibro-sage-dark" />
              {t.label}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
